import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api, { API_BASE, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Leaf, ArrowLeft, Upload as UploadIcon, Image as ImageIcon, Trash2, X, Star, Tag as TagIcon } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export function PhotoGrid({ photos, onDelete, adminMode, onImageClick, families = [], onTagChange }) {
  if (!photos.length) {
    return (
      <div className="bg-white border border-[#e6e2d6] rounded-2xl p-14 text-center text-stone-500">
        <ImageIcon className="h-8 w-8 mx-auto mb-3 text-stone-400" />
        No photos yet — be the first to add one.
      </div>
    );
  }
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
      {photos.map((p) => {
        const taggedFam = families.find((f) => f._id === p.tagged_family_id);
        return (
          <div
            key={p.id}
            className="group relative bg-white border border-[#e6e2d6] rounded-2xl overflow-hidden card-lift"
            data-testid={`photo-${p.id}`}
          >
            <button
              type="button"
              onClick={() => onImageClick && onImageClick(p)}
              className="block w-full aspect-square bg-stone-100"
            >
              <img
                src={`${BACKEND_URL}${p.url}`}
                alt={p.caption || "family photo"}
                className="w-full h-full object-cover"
                loading="lazy"
              />
              {p.is_hero && (
                <span className="absolute top-2 left-2 bg-[#1a2947] text-white rounded-full px-2 py-0.5 text-[10px] font-semibold flex items-center gap-1">
                  <Star className="h-3 w-3" /> Hero
                </span>
              )}
            </button>
            <div className="p-3 space-y-2">
              {p.caption && (
                <div className="text-sm text-stone-700 line-clamp-2">
                  {p.caption}
                </div>
              )}
              <div className="text-xs text-stone-500 flex items-center justify-between">
                <span>{p.uploader_name || "Family"}</span>
                {adminMode && (
                  <button
                    onClick={() => onDelete && onDelete(p.id)}
                    className="text-stone-400 opacity-0 group-hover:opacity-100 hover:text-red-600 transition-opacity"
                    data-testid={`delete-photo-${p.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
              {adminMode && (
                <div className="pt-2 border-t border-[#eae5d9] space-y-2">
                  <div className="flex items-center gap-1 text-xs text-stone-500">
                    <TagIcon className="h-3 w-3" /> Tag to family
                  </div>
                  <Select
                    value={p.tagged_family_id || "__none__"}
                    onValueChange={(v) =>
                      onTagChange && onTagChange(p.id, {
                        tagged_family_id: v === "__none__" ? null : v,
                        is_hero: p.is_hero && v !== "__none__",
                      })
                    }
                  >
                    <SelectTrigger className="h-8 text-xs bg-stone-50 border-stone-200" data-testid={`tag-select-${p.id}`}>
                      <SelectValue placeholder="Not tagged" />
                    </SelectTrigger>
                    <SelectContent className="bg-white max-h-64">
                      <SelectItem value="__none__">Not tagged</SelectItem>
                      {families.map((f) => (
                        <SelectItem key={f._id} value={f._id}>
                          {`${f.first_name || ""} ${f.last_name || ""}`.trim() || "Unnamed"}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {taggedFam && (
                    <label className="flex items-center gap-2 text-xs text-stone-600 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!!p.is_hero}
                        onChange={(e) =>
                          onTagChange && onTagChange(p.id, {
                            tagged_family_id: p.tagged_family_id,
                            is_hero: e.target.checked,
                          })
                        }
                        data-testid={`hero-checkbox-${p.id}`}
                      />
                      Use as hero photo for directory PDF
                    </label>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function PhotoUploader({ endpoint, headers, onUploaded, testid = "photo-upload" }) {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [caption, setCaption] = useState("");
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);

  const pick = (f) => {
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      toast.error("Please pick an image file.");
      return;
    }
    if (f.size > 10 * 1024 * 1024) {
      toast.error("Max 10 MB.");
      return;
    }
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const submit = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("caption", caption);
      const res = await fetch(`${API_BASE}${endpoint}`, {
        method: "POST",
        credentials: "include",
        headers: headers || {},
        body: form,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Upload failed");
      toast.success("Uploaded");
      setFile(null);
      setCaption("");
      setPreview(null);
      if (fileRef.current) fileRef.current.value = "";
      onUploaded && onUploaded(data);
    } catch (e) {
      toast.error(e.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="bg-white border border-[#e6e2d6] rounded-2xl p-5">
      <input
        type="file"
        accept="image/*"
        ref={fileRef}
        onChange={(e) => pick(e.target.files?.[0])}
        className="hidden"
        data-testid={`${testid}-input`}
      />
      {!file ? (
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          className="w-full border-2 border-dashed border-[#e6e2d6] hover:border-[#1a2947] rounded-xl px-6 py-8 text-center transition-colors"
          data-testid={`${testid}-pick-btn`}
        >
          <UploadIcon className="h-6 w-6 mx-auto mb-2 text-stone-400" />
          <div className="font-medium text-stone-700">Add a photo</div>
          <div className="text-xs text-stone-500 mt-1">
            JPG / PNG / WEBP · up to 10 MB
          </div>
        </button>
      ) : (
        <div className="space-y-3">
          <div className="relative rounded-xl overflow-hidden">
            <img
              src={preview}
              alt="preview"
              className="w-full max-h-64 object-cover"
            />
            <button
              onClick={() => {
                setFile(null);
                setPreview(null);
                if (fileRef.current) fileRef.current.value = "";
              }}
              className="absolute top-2 right-2 bg-black/50 text-white rounded-full p-1 hover:bg-black/70"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <Input
            placeholder="Caption (optional)"
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            className="bg-stone-50 border-stone-200"
            data-testid={`${testid}-caption`}
          />
          <div className="flex justify-end gap-2">
            <Button
              variant="ghost"
              onClick={() => {
                setFile(null);
                setPreview(null);
                setCaption("");
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={submit}
              disabled={uploading}
              className="rounded-full bg-[#1a2947] hover:bg-[#0f1a2e] btn-pill"
              data-testid={`${testid}-submit`}
            >
              {uploading ? "Uploading..." : "Add photo"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Gallery({ adminMode = false }) {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [families, setFamilies] = useState([]);

  const load = async () => {
    try {
      const r = await api.get("/photos");
      setPhotos(r.data);
    } catch {
      toast.error("Failed to load photos");
    }
  };

  useEffect(() => {
    load().finally(() => setLoading(false));
    if (adminMode) {
      api
        .get("/families")
        .then((r) => setFamilies(r.data || []))
        .catch(() => {});
    }
  }, [adminMode]);

  const remove = async (id) => {
    try {
      await api.delete(`/photos/${id}`);
      toast.success("Photo removed");
      setConfirmDelete(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  const tagPhoto = async (photoId, { tagged_family_id, is_hero }) => {
    try {
      await api.patch(`/photos/${photoId}`, {
        tagged_family_id: tagged_family_id || null,
        is_hero: !!is_hero,
      });
      toast.success("Tag saved");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to tag");
    }
  };

  const headers = adminMode
    ? { Authorization: `Bearer ${localStorage.getItem("admin_token") || ""}` }
    : {};

  return (
    <div className="min-h-screen bg-[#fdfbf7]">
      <header className="border-b border-[#e6e2d6] bg-white/70 backdrop-blur">
        <div className="max-w-6xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <Link to="/admin" className="flex items-center gap-2">
            <img src="/bailey-logo.png" alt="Bailey Family Reunion" className="h-9 w-9 rounded-full object-cover ring-2 ring-[#1a2947]/10" />
            <span className="font-serif text-xl text-stone-900">Bailey Family Reunion</span>
            <span className="label-eyebrow text-stone-400 ml-2">Gallery</span>
          </Link>
          <Link
            to="/admin"
            className="label-eyebrow text-stone-500 hover:text-[#1a2947] flex items-center gap-1"
            data-testid="gallery-back-link"
          >
            <ArrowLeft className="h-4 w-4" /> Families
          </Link>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-6 md:px-10 py-10">
        <div className="mb-8">
          <div className="label-eyebrow mb-2">Memories</div>
          <h1 className="font-serif text-4xl md:text-5xl text-stone-900 tracking-tight">
            Family Album
          </h1>
          <p className="text-stone-600 mt-3 max-w-xl">
            A shared album where everyone can drop a photo. Click any image to
            see it big.
          </p>
        </div>

        <div className="mb-6">
          <PhotoUploader
            endpoint="/photos"
            headers={headers}
            onUploaded={load}
            testid="admin-photo-upload"
          />
        </div>

        {loading ? (
          <div className="text-stone-500">Loading...</div>
        ) : (
          <PhotoGrid
            photos={photos}
            adminMode={adminMode}
            families={families}
            onDelete={(id) => setConfirmDelete(id)}
            onImageClick={(p) => setLightbox(p)}
            onTagChange={tagPhoto}
          />
        )}
      </main>

      <Dialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Remove this photo?</DialogTitle>
          </DialogHeader>
          <p className="text-stone-600">This can't be undone.</p>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmDelete(null)}>Cancel</Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white rounded-full"
              onClick={() => remove(confirmDelete)}
              data-testid="confirm-photo-delete-btn"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!lightbox} onOpenChange={(v) => !v && setLightbox(null)}>
        <DialogContent className="bg-black border-none max-w-4xl p-0">
          {lightbox && (
            <img
              src={`${BACKEND_URL}${lightbox.url}`}
              alt={lightbox.caption || ""}
              className="w-full max-h-[85vh] object-contain"
            />
          )}
          {lightbox?.caption && (
            <div className="text-white text-center text-sm p-4 bg-black">
              {lightbox.caption}
              <div className="text-stone-400 text-xs mt-1">
                — {lightbox.uploader_name}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
